import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Briefcase, Bell, User, LogOut, Menu, X, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navbar = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Erreur lors de la récupération des notifications:', error);
    }
  };

  const handleLogout = () => {
    onLogout();
    navigate('/');
  };

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
            <Briefcase className="h-8 w-8 text-sky-500" strokeWidth={2} />
            <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Mosala<span className="text-sky-500">.cd</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/jobs" data-testid="nav-jobs-link">
              <Button variant="ghost" className="text-slate-700 hover:text-sky-600">
                Offres d'emploi
              </Button>
            </Link>

            <Link to="/tarifs" data-testid="nav-tarifs-link">
              <Button variant="ghost" className="text-slate-700 hover:text-yellow-600">
                Tarifs
              </Button>
            </Link>

            {user ? (
              <>
                {/* Premium Button */}
                <Link to="/premium" data-testid="nav-premium-link">
                  <Button 
                    variant="ghost" 
                    className={`${user.is_premium 
                      ? 'text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50' 
                      : 'text-slate-700 hover:text-yellow-600'
                    }`}
                  >
                    <Crown className={`h-4 w-4 mr-1 ${user.is_premium ? 'text-yellow-500' : ''}`} />
                    {user.is_premium ? 'Premium' : 'Devenir Premium'}
                  </Button>
                </Link>

                <Link to="/dashboard" className="relative" data-testid="nav-notifications-link">
                  <Button variant="ghost" size="icon" className="relative">
                    <Bell className="h-5 w-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center pulse-notification" data-testid="notification-badge">
                        {unreadCount}
                      </span>
                    )}
                  </Button>
                </Link>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="relative" data-testid="user-menu-trigger">
                      <User className="h-5 w-5" />
                      {user.is_premium && (
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full border-2 border-white"></span>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <div className="px-2 py-1.5">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{user.nom}</p>
                        {user.is_premium && (
                          <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                            <Crown className="h-3 w-3 mr-1" />
                            Premium
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="menu-dashboard">
                      <User className="mr-2 h-4 w-4" />
                      Mon tableau de bord
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate('/premium')} data-testid="menu-premium">
                      <Crown className="mr-2 h-4 w-4 text-yellow-500" />
                      {user.is_premium ? 'Avantages Premium' : 'Passer à Premium'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                      <LogOut className="mr-2 h-4 w-4" />
                      Déconnexion
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Link to="/auth" data-testid="nav-auth-link">
                <Button className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-6">
                  Connexion
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 space-y-2" data-testid="mobile-menu">
            <Link to="/jobs" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start">
                Offres d'emploi
              </Button>
            </Link>
            <Link to="/tarifs" onClick={() => setMobileMenuOpen(false)}>
              <Button variant="ghost" className="w-full justify-start">
                <Crown className="h-4 w-4 mr-2 text-yellow-500" />
                Tarifs
              </Button>
            </Link>
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start">
                    Mon tableau de bord
                  </Button>
                </Link>
                <Link to="/premium" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start">
                    <Crown className={`h-4 w-4 mr-2 ${user.is_premium ? 'text-yellow-500' : ''}`} />
                    {user.is_premium ? 'Avantages Premium' : 'Passer à Premium'}
                  </Button>
                </Link>
                <Button 
                  variant="ghost" 
                  className="w-full justify-start" 
                  onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                >
                  Déconnexion
                </Button>
              </>
            ) : (
              <Link to="/auth" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white rounded-full">
                  Connexion
                </Button>
              </Link>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
